
import { useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import type { BlogPostInput } from "@/types/blog";

const CreateStormDamageBlogPost = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const insertBlogPost = async () => {
    setIsSubmitting(true);
    
    const blogPost: BlogPostInput = {
      title: "Understanding Storm Damage to Your Roof: A Guide for Oklahoma City Homeowners",
      slug: "storm-damage-roof-guide-okc",
      meta_description: "Learn how to identify, prevent, and address storm damage to your roof in Oklahoma City. Expert tips and guidance for protecting your home.",
      introduction: "Oklahoma City homeowners face unique challenges when it comes to protecting their roofs from storm damage. From severe thunderstorms to tornadoes and hail, our local weather patterns can take a significant toll on residential roofing systems. This comprehensive guide will help you understand, identify, and address storm damage to ensure your roof continues to protect your home effectively.",
      local_relevance: "Oklahoma City's position in 'Tornado Alley' and its exposure to severe weather events makes understanding storm damage particularly crucial for local homeowners. Our unique climate patterns, including severe thunderstorms, hail, and high winds, create specific challenges for roof maintenance and protection.",
      main_content: {
        sections: [
          {
            title: "Common Types of Storm Damage in Oklahoma City",
            content: "Oklahoma City roofs frequently face several types of storm-related damage. Wind damage can lift or remove shingles, creating vulnerable spots in your roof's protection. Hail damage often leaves visible dents and marks, weakening the roofing material over time. Heavy rainfall can expose existing vulnerabilities, leading to leaks and interior water damage. Additionally, debris impact during storms can cause immediate structural damage requiring prompt attention."
          },
          {
            title: "Immediate Steps After Storm Damage",
            content: "After a storm passes, conduct a preliminary visual inspection from the ground level. Look for missing shingles, visible dents, or debris on your roof. Document any visible damage with photos for insurance purposes. If you notice significant damage or leaks, contact a qualified local roofing contractor immediately for a professional inspection. Remember to prioritize safety and avoid climbing onto your roof, especially after severe weather events."
          },
          {
            title: "Long-term Prevention Strategies",
            content: "Regular maintenance is key to preventing severe storm damage. Schedule professional roof inspections twice yearly, ideally before and after storm season. Consider upgrading to impact-resistant roofing materials specifically designed for Oklahoma's severe weather conditions. Keep trees trimmed away from your roof and ensure your gutters and drainage systems are clear and functioning properly to prevent water accumulation during heavy rainfall."
          }
        ]
      },
      common_problems: {
        problems: [
          {
            title: "Missing or Damaged Shingles",
            description: "High winds and hail can remove or damage roofing shingles, exposing your roof to potential water damage.",
            solution: "Schedule prompt repairs with a qualified contractor to replace missing or damaged shingles before the next storm."
          },
          {
            title: "Water Leaks",
            description: "Storm damage can create entry points for water, leading to interior damage and potential mold growth.",
            solution: "Identify leak sources through professional inspection and repair damaged areas immediately to prevent further water infiltration."
          },
          {
            title: "Gutter Damage",
            description: "Storms can damage or clog gutters, preventing proper water drainage from your roof.",
            solution: "Regular gutter cleaning and maintenance, plus installation of gutter guards to prevent debris accumulation."
          }
        ]
      },
      pro_tips: {
        tips: [
          {
            title: "Documentation is Critical",
            description: "Maintain a detailed record of your roof's condition with regular photos and inspection reports to support potential insurance claims."
          },
          {
            title: "Choose Local Expertise",
            description: "Work with roofing contractors who understand Oklahoma City's specific weather challenges and building codes."
          },
          {
            title: "Preventive Maintenance",
            description: "Invest in regular maintenance to identify and address potential vulnerabilities before major storms hit."
          }
        ]
      },
      professional_advice: "When selecting a roofing contractor for storm damage repairs, ensure they have experience with insurance claims and are familiar with Oklahoma's specific building codes. Look for contractors who offer emergency services and can provide temporary protection measures while planning permanent repairs.",
      cta_text: "Don't wait until storm damage worsens. Contact a qualified Oklahoma City roofing contractor today for a professional inspection and protect your home investment.",
      final_thoughts: "Storm damage to your roof requires prompt, professional attention to prevent more extensive problems. By understanding the signs of damage and maintaining a relationship with a trusted local contractor, you can protect your home and maintain your roof's integrity through Oklahoma City's challenging weather conditions.",
      service_type: "roofing",
      images: {
        featured: "/path/to/featured-image.jpg",
        gallery: [
          "/path/to/storm-damage-1.jpg",
          "/path/to/repair-process.jpg",
          "/path/to/prevention-tips.jpg"
        ]
      },
      keywords: [
        "storm damage roof oklahoma city",
        "roof repair OKC",
        "storm protection roofing",
        "oklahoma roofing contractor",
        "hail damage repair",
        "emergency roof repair oklahoma city",
        "roof maintenance OKC",
        "storm season preparation"
      ],
      published_at: new Date().toISOString()
    };

    try {
      const { error } = await supabase
        .from('blog_posts')
        .insert(blogPost);

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: "Blog post created successfully",
      });

    } catch (error) {
      console.error('Error creating blog post:', error);
      toast({
        title: "Error",
        description: "Failed to create blog post. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Create Storm Damage Blog Post</h1>
      <button 
        onClick={insertBlogPost}
        disabled={isSubmitting}
        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
      >
        {isSubmitting ? "Creating..." : "Create Blog Post"}
      </button>
    </div>
  );
};

export default CreateStormDamageBlogPost;
